import { useEffect, useRef, useState } from "react";
import { Autocomplete, CircularProgress, TextField, Box } from "@mui/material";
import { usePlansList } from "@/api/hooks/usePlans";

// Custom debounce function
function useDebouncedCallback<T extends (...args: any[]) => void>(callback: T, delay: number) {
    const timer = useRef<NodeJS.Timeout | null>(null);
    return (...args: Parameters<T>) => {
        if (timer.current) clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            callback(...args);
        }, delay);
    };
}

interface AsyncPlanSelectProps {
    value: string;
    onChange: (value: string) => void;
    label?: string;
    error?: string;
}

export default function AsyncPlanSelect({ value, onChange, label = "Plan", error }: AsyncPlanSelectProps) {
    const [search, setSearch] = useState("");
    const [inputValue, setInputValue] = useState("");

    // Debounced search
    const debouncedSetSearch = useDebouncedCallback(setSearch, 400);

    // Fetch all plans (for demo; ideally, backend should support search param)
    const { data, isLoading } = usePlansList({});
    const allOptions =
        data?.data?.items
            ?.filter((p: any) => p.isActive)
            .map((p: any) => ({
                label: p.ml_title?.en || p.ml_title?.ar || `Plan ${p.id}`,
                value: String(p.id),
            })) || [];
    const options = search ? allOptions.filter((o) => o.label.toLowerCase().includes(search.toLowerCase())) : allOptions;

    return (
        <Box>
            <Autocomplete
                options={options}
                loading={isLoading}
                value={options.find((o) => o.value === value) || null}
                inputValue={inputValue}
                onInputChange={(_, newInputValue) => {
                    setInputValue(newInputValue);
                    debouncedSetSearch(newInputValue);
                }}
                onChange={(_, newValue) => {
                    if (newValue) {
                        onChange(newValue.value);
                    } else {
                        onChange("");
                    }
                }}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, val) => option.value === val.value}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        error={!!error}
                        helperText={error}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
        </Box>
    );
}
